/**
 * §11's disclosure: when a filter or period range is active, a tab's total is a different measurement
 * from the page's KPI tile above it, and the screen must say so.
 *
 * This page has already shipped the failure once — the gross-sum tile beside the signed result, two
 * numbers on one screen with nothing explaining the gap, reported as a defect. Shaping reintroduces
 * exactly that shape, so every tab labels its total as filtered and prints the unfiltered consolidated
 * total beside it as the reference the reader can anchor on.
 *
 * One component rather than four copies: a tab that forgot the disclosure would look like a tab whose
 * filter simply matched everything.
 */

import { TableCell, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '@/lib/locale';

/** Suffixes a total's label with "(filtered)" while shaping is active — never silently. */
export function useFilteredTotalLabel() {
  const { t } = useTranslation();
  return (label: string, isShaped: boolean) =>
    isShaped
      ? t('Finance.PlanRollup.TotalFilteredSuffix', { defaultValue: '{{label}} (filtered)', label })
      : label;
}

interface Props {
  /** From the response, not from the request: only the server knows whether a filter actually applied. */
  isShaped: boolean;
  /** The page-level consolidated total with no shaping — the anchor the shaped figure is read against. */
  unfilteredTotal: number | undefined;
  /** Columns to span in the tab's own footer. */
  colSpan: number;
}

export function RollupUnfilteredTotalRow({ isShaped, unfilteredTotal, colSpan }: Props) {
  const { t } = useTranslation();
  if (!isShaped) return null;

  return (
    <TableRow>
      <TableCell colSpan={colSpan} sx={{ borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">
          {t('Finance.PlanRollup.UnfilteredReference', {
            defaultValue: 'Unfiltered consolidated total: {{total}} — the figure in the tile above, which the filter and period range do not narrow.',
            total: formatNumber(unfilteredTotal ?? 0, { maximumFractionDigits: 0 }),
          })}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

export default RollupUnfilteredTotalRow;
